import { Components, Palette } from '@mui/material/styles';
import { TreeItemClassKey } from '@mui/lab';
import { CSSProperties } from '@mui/styles';

// https://github.com/mui-org/material-ui/issues/12164#issuecomment-564041219
interface Overrides extends Components {
  MuiTreeItem?:
    | Partial<Record<TreeItemClassKey, CSSProperties | (() => CSSProperties)>>
    | undefined;
}

export const getOverrides = (palette: Palette): Overrides => ({
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        // При переходе на MUI5 изменился fontSize
        // https://mui.com/material-ui/migration/v5-component-changes/#update-body-font-size
        // Нам это не подходит, потому что все становится слишком большим
        fontSize: '0.875rem',
      },
      '& .MuiDataGrid-panelWrapper .MuiFormControl-root': {
        margin: '0',
      },
      // @material-ui/pickers часы и минуты в Popover,
      // Чтобы по размеру сочеталось с размером месяца и дня.
      'h3.MuiPickersToolbarText-toolbarTxt': {
        fontSize: '2.5rem',
      },
      // @material-ui/pickers месяц и день в Popover,
      // Без этого русский перевод не влазит и ломает верстку.
      'h4.MuiPickersToolbarText-toolbarTxt': {
        fontSize: '1.6rem',
      },
      // @material-ui/pickers в Popover для KeyboardDateTimePicker
      // Баг с длиной подчеркивания таба при открытии (до взаимодействия с ним).
      '.MuiPickerDTTabs-tabs .MuiTabs-indicator': {
        width: '50% !important',
      },
      // Стили нумерации строк ace editor
      '#ace-editor .ace_gutter': {
        backgroundColor: palette.primary.main,
        color: palette.primary.contrastText,
      },
      // Скрыть курсор после blur-а ace editor
      '#ace-editor .ace_hidden-cursors': {
        display: 'none',
      },
      // Скролл внизу залазит на текст ace editor
      '#ace-editor .ace_scrollbar-h': {
        height: '7px !important',
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      variant: 'standard',
    },
    styleOverrides: {
      root: {
        width: 328,
        maxWidth: '100%',
      },
    },
  },
  MuiIconButton: {
    defaultProps: {
      size: 'large',
    },
    styleOverrides: {
      root: {
        color: palette.grey.A100,
        '&.Mui-disabled': {
          color: palette.grey.A400,
        },
      },
    },
  },
  MuiRadio: {
    defaultProps: {
      color: 'secondary',
    },
    styleOverrides: {
      colorSecondary: {
        '&.Mui-disabled': {
          color: palette.grey.A400,
        },
      },
      root: {
        color: palette.primary.main,
      },
    },
  },
  MuiTypography: {
    styleOverrides: {
      h6: {
        fontWeight: 500,
      },
      h5: {
        fontWeight: 500,
      },
      subtitle1: {
        fontWeight: 500,
        margin: '16px 0 8px 0',
      },
      subtitle2: {
        fontWeight: 500,
      },
      root: ({ ownerState }) => ({
        ...(ownerState.color === 'textPrimary' && {
          color: palette.info.main,
        }),
        ...(ownerState.color === 'textSecondary' && {
          color: palette.grey.A200,
        }),
      }),
    },
  },
  MuiLink: {
    defaultProps: {
      underline: 'hover',
    },
    styleOverrides: {
      root: {
        color: palette.info.main,
      },
    },
  },
  MuiFormLabel: {
    defaultProps: {
      focused: false,
    },
    styleOverrides: {
      root: {
        color: palette.grey.A100,
        fontSize: '0.875rem',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        '&.Mui-focused': {
          color: palette.secondary.main,
        },
        '&.Mui-disabled': {
          color: palette.grey.A400,
        },
      },
    },
  },
  MuiFormControlLabel: {
    styleOverrides: {
      root: {
        width: 'max-content',
        margin: 0,
      },
      label: {
        fontSize: '0.875rem',
        '&.Mui-disabled': {
          color: palette.grey.A400,
        },
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      outlined: {
        '&.Mui-shrink': {
          transform: 'translate(14px, -6px) scale(0.85)',
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: 2,
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: palette.secondary.main,
        },
        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
          borderColor: palette.grey.A400,
        },
      },
      notchedOutline: {
        borderColor: palette.grey.A100,
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        boxShadow:
          '0px 1px 3px rgba(0, 0, 0, 0.2), 0px 2px 2px rgba(0, 0, 0, 0.12), 0px 0px 2px rgba(0, 0, 0, 0.14)',
        margin: '8px 0 16px 0',
      },
      containedPrimary: {
        backgroundColor: palette.background.paper,
        color: palette.primary.main,
        '&.Mui-disabled': {
          backgroundColor: palette.background.paper,
          boxShadow:
            '0px 1px 3px rgba(0, 0, 0, 0.2), 0px 2px 2px rgba(0, 0, 0, 0.12), 0px 0px 2px rgba(0, 0, 0, 0.14)',
          color: palette.grey.A400,
        },
        '&:hover': {
          backgroundColor: palette.grey.A400,
          boxShadow:
            '0px 1px 3px rgba(0, 0, 0, 0.2), 0px 2px 2px rgba(0, 0, 0, 0.12), 0px 0px 2px rgba(0, 0, 0, 0.14)',
        },
      },
      containedSecondary: {
        color: palette.secondary.contrastText,
        '&.Mui-disabled': {
          backgroundColor: palette.background.paper,
          boxShadow:
            '0px 1px 3px rgba(0, 0, 0, 0.2), 0px 2px 2px rgba(0, 0, 0, 0.12), 0px 0px 2px rgba(0, 0, 0, 0.14)',
          color: palette.grey.A400,
        },
      },
      outlined: {
        boxShadow: 'none',
      },
      text: {
        boxShadow: 'none',
      },
    },
  },
  MuiInput: {
    styleOverrides: {
      // Для изменения цвета подчеркивания на secondary для TextField variant="standard" (по умолчанию)
      underline: {
        '&:hover:not($disabled):before': {
          borderBottom: `2px solid ${palette.secondary.main}`,
        },
        '&::after': {
          borderColor: palette.secondary.main,
        },
      },
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: {
        '&.Mui-disabled': {
          color: palette.grey.A400,
        },
        '& input::placeholder': {
          color: palette.grey.A400,
          opacity: 1,
        },
      },
      input: {
        fontSize: '0.875rem',
      },
    },
  },
  MuiAutocomplete: {
    styleOverrides: {
      root: {
        // Правый отступ для иконки раскрытия при size="small"
        // Баг исправлен в https://github.com/mui/material-ui/pull/35640 (MUI >5.11.0)
        '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiAutocomplete-input': {
          paddingRight: 30,
        },
      },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        color: palette.grey.A200,
        '&.Mui-disabled': {
          color: palette.grey.A200,
        },
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      textColorInherit: {
        color: palette.grey.A100,
        opacity: 1,
        '&.Mui-selected': {
          color: palette.secondary.main,
        },
      },
    },
  },
  MuiCheckbox: {
    defaultProps: {
      color: 'secondary',
    },
    styleOverrides: {
      root: {
        color: palette.primary.main,
      },
      colorSecondary: {
        '&.Mui-disabled': {
          color: palette.grey.A400,
        },
      },
    },
  },
  MuiSwitch: {
    defaultProps: {
      color: 'secondary',
    },
    styleOverrides: {
      colorSecondary: {
        '&.Mui-disabled': {
          color: palette.grey.A400,
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        backgroundColor: palette.background.default,
        color: palette.secondary.contrastText,
        fontSize: '0.8rem', // У FF съезжает почему-то текст, когда его размер 0.8125rem, который указан по умолчанию..
      },
      deleteIcon: {
        color: palette.secondary.contrastText,
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        // Без overflow visible абсолютно спозиционированный элемент относительно дочернего элемента скроется.
        // Например, лейбл текстового поля.
        overflow: 'visible',
        padding: 0,
        paddingBottom: 24,
        '&:first-child': {
          paddingTop: 0,
        },
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: 0,
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        padding: 0,
        '& .MuiTypography-root': {
          padding: 0,
          margin: 0,
        },
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        padding: 24,
        paddingBottom: 8,
      },
    },
  },
  MuiStepConnector: {
    styleOverrides: {
      alternativeLabel: {
        top: 25,
        marginRight: 15,
        marginLeft: 15,
      },
    },
  },
  MuiStepIcon: {
    styleOverrides: {
      root: {
        color: palette.secondary.main,
        width: 50,
        height: 50,
      },
      text: {
        fill: 'black',
        fontWeight: 'bold',
        fontSize: 10,
      },
    },
  },
  MuiStepLabel: {
    styleOverrides: {
      root: {
        color: 'black',
        fontWeight: 'bold',
      },
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: {
        backgroundColor: palette.grey.A400,
      },
    },
  },
});
